import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, Zap, Rocket, X } from "lucide-react";

const FeatureCtx = createContext(null);

export function FeatureProvider({ children }) {
  const [features, setFeatures] = useState({ plan: "free", flags: {}, limits: {} });
  const [paywall, setPaywall] = useState(null); // {flag, title, description}

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/me/features"); setFeatures(data); } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("bf_token");
    if (token) refresh();
  }, [refresh]);

  const hasFeature = (flag) => Boolean(features.flags?.[flag]);
  const requireFeature = (flag, { title, description } = {}) => {
    if (hasFeature(flag)) return true;
    setPaywall({ flag, title: title || "Funzione Premium", description });
    return false;
  };

  return (
    <FeatureCtx.Provider value={{ features, hasFeature, requireFeature, refresh }}>
      {children}
      {paywall && <PaywallModal paywall={paywall} onClose={() => setPaywall(null)}/>}
    </FeatureCtx.Provider>
  );
}

export const useFeatures = () => useContext(FeatureCtx);

const FLAG_LABELS = {
  export_docx: ["Export DOCX", "Esporta ebook in Microsoft Word"],
  export_epub: ["Export EPUB", "Esporta ebook per Kindle e altri lettori"],
  cover_studio: ["Cover Studio", "Genera cover AI con 4 varianti"],
  marketing_kit: ["Marketing Kit", "Email, 10 post social, landing e descrizione Amazon"],
  kdp_tools: ["KDP Tools", "Formati compatibili Amazon KDP, keyword e categorie"],
  brand_kit: ["Brand Kit", "Logo, palette, font applicati automaticamente"],
  premium_templates: ["Template premium", "Layout professionali editoriali"],
  ai_assistant: ["AI Writing Assistant", "Riscrivi, migliora, espandi con AI"],
  advanced_cover: ["Cover Studio avanzato", "Mockup 3D, varianti multiple"],
  book_analysis: ["Analisi libro", "Coerenza, ripetizioni, qualità"],
  no_watermark: ["Nessun watermark", "Export puliti pronti alla vendita"],
};

function PaywallModal({ paywall, onClose }) {
  const nav = useNavigate();
  const [label, desc] = FLAG_LABELS[paywall.flag] || [paywall.title, paywall.description];
  return (
    <div data-testid="paywall-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bf-card p-6 max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400"><X className="w-4 h-4"/></button>
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center mb-4">
          <Rocket className="w-6 h-6 text-[#080B12]"/>
        </div>
        <h3 className="font-sans text-2xl mb-2">Sblocca {label}</h3>
        <p className="text-sm text-slate-400 mb-5">{desc || paywall.description || "Questa funzione è disponibile dal piano Pro in su."}</p>
        <ul className="space-y-2 mb-6 text-sm">
          <li className="flex items-start gap-2"><Zap className="w-4 h-4 text-[#6366F1] mt-0.5"/><span className="text-slate-300">120.000 parole AI/mese + 300 crediti AI</span></li>
          <li className="flex items-start gap-2"><Zap className="w-4 h-4 text-[#6366F1] mt-0.5"/><span className="text-slate-300">Export PDF, DOCX, EPUB senza watermark</span></li>
          <li className="flex items-start gap-2"><Zap className="w-4 h-4 text-[#6366F1] mt-0.5"/><span className="text-slate-300">Cover Studio, Marketing Kit, KDP Tools</span></li>
        </ul>
        <div className="flex gap-2">
          <button data-testid="paywall-upgrade-btn" onClick={() => { onClose(); nav("/app/billing"); }} className="bf-btn-gold flex-1 py-2.5 rounded-lg text-sm">Passa a Pro</button>
          <button onClick={onClose} className="bf-btn-outline px-4 py-2.5 rounded-lg text-sm">Più tardi</button>
        </div>
      </div>
    </div>
  );
}
